// VerifyOtp.jsx
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/Api';
import { completeBuyerAuthentication } from '../../../utils/buyerAuthFlow';

const VerifyOtp = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { email, message } = location.state || {};

    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(600); // 10 minutes in seconds
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        if (!email) {
            navigate('/signup');
        }
    }, [email, navigate]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimer((prevTimer) => {
                if (prevTimer <= 1) {
                    setCanResend(true);
                    clearInterval(interval);
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleChange = (element, index) => {
        const value = element.value;

        // Handle paste (multiple characters)
        if (value.length > 1) {
            const pastedOtp = value.replace(/\D/g, '').slice(0, 6); // Remove non-digits and limit to 6
            const newOtp = [...otp];

            for (let i = 0; i < pastedOtp.length; i++) {
                if (index + i < 6) {
                    newOtp[index + i] = pastedOtp[i];
                }
            }

            setOtp(newOtp);

            // Focus on the last filled input or next empty
            const lastIndex = Math.min(index + pastedOtp.length, 5);
            document.getElementById(`otp-${lastIndex}`).focus();
            return;
        }

        // Handle single character
        if (isNaN(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move to next input if value is entered and not the last input
        if (value && index < 5) {
            document.getElementById(`otp-${index + 1}`).focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`).focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const otpString = otp.join('');

        if (otpString.length !== 6) {
            setError('Please enter complete OTP');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Correct axios syntax - remove method, headers, and body
            const response = await axios.post(`${API_URL.api}/verifyOtp1`, {
                email: email,
                otp: otpString,
            });

            // With axios, the data is directly in response.data
            if (response.status === 200 || response.status === 201) {
                await completeBuyerAuthentication(response.data, {
                    successMessage: response.data.message || 'Login successful',
                });
            } else {
                setError(response.data.message || 'Invalid OTP');
            }
        } catch (error) {
            // Improved error handling for axios
            if (error.response) {
                // The server responded with an error status
                setError(error.response.data.message || 'Invalid OTP');
            } else if (error.request) {
                // The request was made but no response was received
                setError('No response from server. Please try again.');
            } else {
                // Something else happened
                setError('Network error. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setCanResend(false);
        setTimer(600);
        setError('');

        try {
            const response = await fetch('/api/resend-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                throw new Error('Failed to resend OTP');
            }
        } catch (error) {
            setError('Failed to resend OTP. Please try again.');
            setCanResend(true);
        }
    };

    if (!email) return null;

    return (
        <div className="verify-container">
            <div className="verify-card">
                <div className="verify-header">
                    <i className="fas fa-envelope-open-text"></i>
                    <h2>Verify Your Email</h2>
                    {message && <p className="success-message">{message}</p>}
                    <p className="verify-info">
                        We've sent a verification code to<br />
                        <strong>{email}</strong>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="verify-form">
                    <div className="otp-container">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-${index}`}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(e.target, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                className="otp-input-verify"
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>

                    {error && <div className="verify-error">{error}</div>}

                    <div className="timer">
                        <i className="fas fa-clock"></i>
                        Time remaining: {formatTime(timer)}
                    </div>

                    <button
                        type="submit"
                        className="verify-button"
                        disabled={isLoading || timer === 0}
                    >
                        {isLoading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i> Verifying...
                            </>
                        ) : (
                            'Verify Email'
                        )}
                    </button>

                    <div className="resend-section">
                        <p>Didn't receive the code?</p>
                        {canResend ? (
                            <button
                                type="button"
                                onClick={handleResendOtp}
                                className="resend-button"
                            >
                                Resend OTP
                            </button>
                        ) : (
                            <span className="resend-wait">
                                Wait {formatTime(timer)} to resend
                            </span>
                        )}
                    </div>

                    <div className="back-to-register">
                        <a href="/register">
                            <i className="fas fa-arrow-left"></i> Back to Register
                        </a>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VerifyOtp;
